import type { TimelineItem } from '@xxtract/ui';
import type { TFunction } from 'i18next';
import { formatOrderTimestamp } from './format';
import type { TimelineEvent, TreeServiceView } from './orders.api';

/**
 * FR19/FR20: the GTIN-detail timeline spans ALL diensten of this trade item. A dienst with
 * zero events in the raw log is an explicit "geen procesdata bekend" dekkingsgat (FR20) — the
 * portal never derives or invents a status for it. Real events sort chronologically first
 * (oldest first, EXPERIENCE.md); gap markers (no timestamp to sort by) follow at the end.
 */
export function buildGtinTimelineItems(events: TimelineEvent[], services: TreeServiceView[], locale: string, t: TFunction): TimelineItem[] {
  const servicesWithEvents = new Set(events.map((event) => event.serviceCode));

  const realItems: TimelineItem[] = [...events]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .map((event) => ({
      id: String(event.id),
      label: `${event.processCode}/${event.eventCode}`,
      timestamp: formatOrderTimestamp(event.timestamp, locale),
      source: t('orders.timeline.source', { serviceCode: event.serviceCode }),
      status: 'complete' as const,
    }));

  const gapItems: TimelineItem[] = services
    .filter((service) => !servicesWithEvents.has(service.serviceCode))
    .map((service) => ({
      id: `gap-${service.serviceCode}`,
      label: t('orders.gtin.timeline.noProcessData'),
      source: t('orders.timeline.source', { serviceCode: service.serviceCode }),
      status: 'gap' as const,
    }));

  return [...realItems, ...gapItems];
}
